import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { BarChart2, CalendarClock, Newspaper, ListChecks, Mail, MessageCircle, X, TrendingUp, UserRound, Search } from 'lucide-react'
import Button from './Button'
import NeutralButton from './NeutralButton'
import { createContentAlert, fetchContentSourceTypes } from '../services/contentAlertApi'
import { fetchSymbolGroups, fetchStocks, fetchEventTypes, fetchQuantitativeAlertChoices, fetchTechnicalAnalysisAlertChoices, fetchAlertCategory, createAlert } from '../services/screenerApi'
import SecondaryButton from './SecondaryButton'
import Toast from './ui/Toast'

// Alert type configurations mapping - DEPRECATED: Now using API endpoint /api/alerts/alert-category/
// Keeping for reference but no longer used in code
const ALERT_TYPE_CONFIGS_DEPRECATED = {
    "Price Deviation": {
        "value": {
            "max": 100.0,
            "min": 0.1,
            "type": "decimal",
            "default": 3.0,
            "required": true,
            "description": "Deviation threshold percentage (e.g., 3.0 for 3%)"
        },
        "operator": {
            "type": "choice",
            "choices": [">", ">=", "<", "<="],
            "default": ">=",
            "required": true,
            "description": "Comparison operator"
        },
        "parameter1": {
            "type": "string",
            "default": "Price Deviation",
            "options": ["Price Deviation", "Deviation Percentage"],
            "required": true,
            "description": "Parameter name for price deviation"
        },
        "parameter2": {
            "type": "string",
            "default": null,
            "required": false,
            "description": "Not used for price deviation"
        }
    },
    "Gap Trading": {
        "value": {
            "max": 50.0,
            "min": 0.1,
            "type": "decimal",
            "default": 2.5,
            "required": true,
            "description": "Gap threshold percentage (e.g., 2.5 for 2.5%)"
        },
        "operator": {
            "type": "choice",
            "choices": [">", ">=", "<", "<=", "="],
            "default": ">=",
            "required": true,
            "description": "Comparison operator"
        },
        "parameter1": {
            "type": "string",
            "default": "Gap Percentage",
            "options": ["Gap Percentage", "Gap"],
            "required": true,
            "description": "Parameter name for gap percentage"
        },
        "parameter2": {
            "type": "string",
            "default": null,
            "required": false,
            "description": "Not used for gap trading"
        }
    },
    "Moving Average Crossover": {
        "value": {
            "type": "decimal",
            "default": null,
            "required": false,
            "description": "Not used for moving average crossover"
        },
        "operator": {
            "type": "choice",
            "choices": ["crossover", "crossover_above", "crossover_below"],
            "default": "crossover",
            "required": true,
            "description": "Crossover type"
        },
        "parameter1": {
            "type": "choice",
            "choices": [
                "5 days moving average",
                "10 days moving average",
                "15 days moving average",
                "20 days moving average",
                "50 days moving average"
            ],
            "default": "15 days moving average",
            "required": true,
            "description": "Short period moving average"
        },
        "parameter2": {
            "type": "choice",
            "choices": [
                "20 days moving average",
                "50 days moving average",
                "100 days moving average",
                "200 days moving average"
            ],
            "default": "200 days moving average",
            "required": true,
            "description": "Long period moving average"
        }
    },
    "Volume": {
        "value": {
            "min": 0.1,
            "type": "decimal",
            "default": 2.0,
            "required": true,
            "description": "Threshold value: ratio (e.g., 2.0 for 200% of average) or absolute volume (e.g., 10000000)"
        },
        "operator": {
            "type": "choice",
            "choices": [">", ">=", "<", "<="],
            "default": ">",
            "required": true,
            "description": "Comparison operator"
        },
        "parameter1": {
            "type": "choice",
            "choices": ["Volume", "Volume Ratio"],
            "default": "Volume Ratio",
            "required": true,
            "description": "Volume metric type"
        },
        "parameter2": {
            "type": "string",
            "default": "20 days average volume",
            "options": [
                "10 days average volume",
                "20 days average volume",
                "30 days average volume",
                "50 days average volume"
            ],
            "required": false,
            "description": "Period for average volume calculation (e.g., \"20 days average volume\") or null for absolute volume"
        }
    },
    "RSI Divergence": {
        "value": {
            "max": 100,
            "min": 0,
            "type": "decimal",
            "default": 70,
            "required": false,
            "description": "RSI threshold (e.g., 70 for overbought, 30 for oversold). Not used for divergence detection."
        },
        "operator": {
            "type": "choice",
            "choices": [">", "<", "divergence_bullish", "divergence_bearish"],
            "default": ">",
            "required": true,
            "description": "RSI condition type"
        },
        "parameter1": {
            "type": "choice",
            "choices": ["RSI", "RSI Divergence"],
            "default": "RSI",
            "required": true,
            "description": "RSI metric type"
        },
        "parameter2": {
            "type": "string",
            "default": "14",
            "options": ["10", "14", "20", "30"],
            "required": false,
            "description": "RSI period (e.g., \"14\" or \"14 days\"), default is 14"
        }
    },
    "Bollinger Bands": {
        "value": {
            "max": 1.0,
            "min": 0.01,
            "type": "decimal",
            "default": 0.1,
            "required": false,
            "description": "Squeeze threshold percentage (e.g., 0.1 for 10% band width). Only used when operator is \"squeeze\"."
        },
        "operator": {
            "type": "choice",
            "choices": ["touch_upper", "touch_lower", "breakout_above", "breakout_below", "squeeze"],
            "default": "touch_upper",
            "required": true,
            "description": "Bollinger Bands condition type"
        },
        "parameter1": {
            "type": "string",
            "default": "Bollinger Bands",
            "options": ["Bollinger Bands", "BB"],
            "required": true,
            "description": "Parameter name"
        },
        "parameter2": {
            "type": "string",
            "format": "period,std_dev",
            "default": "20,2",
            "required": false,
            "description": "Period and standard deviation (format: \"period,std_dev\", e.g., \"20,2\")"
        }
    }
}

// Step 1: Select Alert Type
const AlertTypeSelector = ({ selectedType, onSelect }) => {
    const types = [
        {
            id: 'quantitative',
            icon: BarChart2,
            title: 'Quantitative Alert',
            description: 'Trigger based on price, volume, or technical indicators.',
            examples: 'Price > $150, RSI > 70, Volume spike'
        },
        {
            id: 'event',
            icon: CalendarClock,
            title: 'Event Alert',
            description: 'Get notified about upcoming corporate events.',
            examples: 'Earnings, Dividends, Splits'
        },
        {
            id: 'content',
            icon: Newspaper,
            title: 'Content Monitoring',
            description: 'Monitor news, social media, and specific sources.',
            examples: 'News mentions, YouTube videos, Tweets'
        },
        {
            id: 'composition',
            icon: ListChecks,
            title: 'Watchlist Composition',
            description: 'Track changes to dynamic watchlists.',
            examples: 'New stock added, Stock removed'
        },
        {
            id: 'technical_analysis',
            icon: TrendingUp,
            title: 'Technical Analysis Alert',
            description: 'AI-powered alerts based on chart patterns and technical analysis.',
            examples: 'Significant reversal pattern, Breakout'
        },
        {
            id: 'analyst_agent',
            icon: UserRound,
            title: 'Analyst Agent Alert',
            description: 'Deploy an autonomous AI agent for deep research and analysis.',
            examples: 'Research industry trends, Sentiment analysis'
        }
    ]
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {types.map(type => (
                <button
                    key={type.id}
                    onClick={() => onSelect(type.id)}
                    className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${selectedType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                        }`}
                >
                    <type.icon className="w-7 h-7 text-blue-600 mb-3" strokeWidth={2.25} />
                    <h3 className="font-semibold text-gray-900 mb-1">{type.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                    <p className="text-xs text-gray-500 bg-white p-2 rounded border border-gray-100">
                        e.g., {type.examples}
                    </p>
                </button>
            ))}
        </div>
    )
}

// Step 2: Select Target
const AlertTargetSelector = ({ target, onChange, alertType }) => {
    const [watchlists, setWatchlists] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Stock search states
    const [stockSearchQuery, setStockSearchQuery] = useState('')
    const [stockResults, setStockResults] = useState([])
    const [stockLoading, setStockLoading] = useState(false)
    const [showStockDropdown, setShowStockDropdown] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
    const stockInputRef = useRef(null)
    const stockDropdownRef = useRef(null)

    // Initialize selected stocks from target if they exist
    const selectedStocks = target.stocks || []

    // Map API response to component format
    const mapWatchlist = (item) => ({
        id: item.id,
        name: item.group_name,
        type: item.is_dynamic ? 'dynamic' : 'static'
    })

    // Fetch watchlists when watchlist type is selected
    useEffect(() => {
        if (target.type === 'watchlist') {
            const loadWatchlists = async () => {
                setLoading(true)
                setError(null)
                try {
                    // For composition alerts, only fetch dynamic watchlists
                    const dynamic = alertType === 'composition'
                    const data = await fetchSymbolGroups(dynamic)
                    const mappedWatchlists = data.map(mapWatchlist)
                    setWatchlists(mappedWatchlists)
                } catch (err) {
                    console.error('Failed to fetch watchlists:', err)
                    setError('Failed to load watchlists')
                    setWatchlists([])
                } finally {
                    setLoading(false)
                }
            }
            loadWatchlists()
        }
    }, [target.type, alertType])

    // Debounced stock search
    useEffect(() => {
        if (target.type !== 'stock') {
            setStockSearchQuery('')
            setStockResults([])
            setShowStockDropdown(false)
            return
        }

        const trimmedQuery = stockSearchQuery.trim()

        if (trimmedQuery === '') {
            setStockResults([])
            setShowStockDropdown(false)
            return
        }

        const timeoutId = setTimeout(async () => {
            setStockLoading(true)
            try {
                const data = await fetchStocks(trimmedQuery)
                // Ensure data is an array - handle different response formats
                const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
                setStockResults(results)
                setShowStockDropdown(true)
            } catch (err) {
                console.error('Failed to fetch stocks:', err)
                setStockResults([])
                setShowStockDropdown(false)
            } finally {
                setStockLoading(false)
            }
        }, 400) // 400ms debounce

        return () => clearTimeout(timeoutId)
    }, [stockSearchQuery, target.type])

    // Handle stock selection
    const handleStockSelect = (stock) => {
        const newStocks = [...selectedStocks, { id: stock.id, symbol: stock.symbol, full_symbol: stock.full_symbol, name: stock.name }]
        onChange({ ...target, stocks: newStocks })
        setStockSearchQuery('')
        setShowStockDropdown(false)
        if (stockInputRef.current) {
            stockInputRef.current.focus()
        }
    }

    // Handle stock removal
    const handleStockRemove = (stockId) => {
        const newStocks = selectedStocks.filter(s => s.id !== stockId)
        onChange({ ...target, stocks: newStocks })
    }

    // Filter out selected stocks from results and limit to 15 items
    // Ensure stockResults is always an array
    const availableStocks = (Array.isArray(stockResults) ? stockResults : [])
        .filter(stock => !selectedStocks.some(selected => selected.id === stock.id))
        .slice(0, 15)

    // Calculate dropdown position based on input element
    const updateDropdownPosition = () => {
        if (stockInputRef.current) {
            const rect = stockInputRef.current.getBoundingClientRect()
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4, // 4px gap
                left: rect.left + window.scrollX,
                width: rect.width
            })
        }
    }

    // Update dropdown position when it should be shown
    useEffect(() => {
        if (showStockDropdown && stockInputRef.current) {
            updateDropdownPosition()
        }
    }, [showStockDropdown, stockSearchQuery, stockResults])

    // Update position on scroll and resize
    useEffect(() => {
        if (!showStockDropdown) return

        const handleUpdate = () => {
            updateDropdownPosition()
        }

        window.addEventListener('scroll', handleUpdate, true)
        window.addEventListener('resize', handleUpdate)

        return () => {
            window.removeEventListener('scroll', handleUpdate, true)
            window.removeEventListener('resize', handleUpdate)
        }
    }, [showStockDropdown])

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                stockDropdownRef.current &&
                !stockDropdownRef.current.contains(event.target) &&
                stockInputRef.current &&
                !stockInputRef.current.contains(event.target)
            ) {
                setShowStockDropdown(false)
            }
        }

        if (showStockDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showStockDropdown])

    return (
        <div className="space-y-6">
            {alertType !== 'composition' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        I want to monitor:
                    </label>
                    <div className="flex space-x-4">
                        <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 w-full">
                            <input
                                type="radio"
                                name="targetType"
                                checked={target.type === 'watchlist'}
                                onChange={() => onChange({ ...target, type: 'watchlist', id: null, name: '', stocks: [] })}
                                className="text-blue-600"
                            />
                            <span>Entire Watchlist</span>
                        </label>
                        <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 w-full">
                            <input
                                type="radio"
                                name="targetType"
                                checked={target.type === 'stock'}
                                onChange={() => onChange({ ...target, type: 'stock', id: null, name: '', stocks: [] })}
                                className="text-blue-600"
                            />
                            <span>Symbols</span>
                        </label>
                        {alertType === 'analyst_agent' && (
                            <label className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 w-full">
                                <input
                                    type="radio"
                                    name="targetType"
                                    checked={target.type === 'broad'}
                                    onChange={() => onChange({ ...target, type: 'broad', id: null, name: 'Broad Market Search', stocks: [] })}
                                    className="text-blue-600"
                                />
                                <div className="flex items-center space-x-2">
                                    <Search className="w-4 h-4 text-blue-500" />
                                    <span>Broad Market Search</span>
                                </div>
                            </label>
                        )}
                    </div>
                </div>
            )}

            {target.type === 'watchlist' ? (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Watchlist
                    </label>
                    <select
                        value={target.id || ''}
                        onChange={(e) => {
                            const wl = watchlists.find(w => w.id === parseInt(e.target.value))
                            if (wl) {
                                onChange({ ...target, id: wl.id, name: wl.name })
                            }
                        }}
                        disabled={loading}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="">
                            {loading ? 'Loading watchlists...' : error ? 'Error loading watchlists' : 'Choose a watchlist...'}
                        </option>
                        {watchlists.map(w => (
                            <option key={w.id} value={w.id}>{w.name} ({w.type})</option>
                        ))}
                    </select>
                    {error && (
                        <p className="text-xs text-red-500 mt-1">{error}</p>
                    )}
                    {!loading && !error && watchlists.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">No watchlists available</p>
                    )}
                </div>
            ) : target.type === 'stock' ? (
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search Symbols
                    </label>

                    {/* Selected Stocks Tags */}
                    {selectedStocks.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {selectedStocks.map((stock) => (
                                <span
                                    key={stock.id}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                >
                                    {stock.full_symbol || stock.symbol}
                                    <button
                                        type="button"
                                        onClick={() => handleStockRemove(stock.id)}
                                        className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                        aria-label={`Remove ${stock.full_symbol || stock.symbol}`}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Stock Search Input */}
                    <div className="relative">
                        <input
                            ref={stockInputRef}
                            type="text"
                            value={stockSearchQuery}
                            onChange={(e) => setStockSearchQuery(e.target.value)}
                            onFocus={() => {
                                if (Array.isArray(stockResults) && stockResults.length > 0 && stockSearchQuery.trim() !== '') {
                                    setShowStockDropdown(true)
                                }
                            }}
                            placeholder="Enter symbol (e.g., AAPL)"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />

                        {/* Loading Indicator */}
                        {stockLoading && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            </div>
                        )}
                    </div>

                    {/* Dropdown Results - Rendered via Portal */}
                    {showStockDropdown && createPortal(
                        <div
                            ref={stockDropdownRef}
                            className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                            style={{
                                top: `${dropdownPosition.top}px`,
                                left: `${dropdownPosition.left}px`,
                                width: `${dropdownPosition.width}px`
                            }}
                        >
                            {availableStocks.length > 0 ? (
                                <>
                                    {availableStocks.map((stock) => (
                                        <button
                                            key={stock.id}
                                            type="button"
                                            onClick={() => handleStockSelect(stock)}
                                            className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors"
                                        >
                                            <div className="font-medium text-gray-900">
                                                {stock.full_symbol || stock.symbol}
                                            </div>
                                            {stock.name && stock.name !== stock.symbol && (
                                                <div className="text-xs text-gray-500">{stock.name}</div>
                                            )}
                                        </button>
                                    ))}
                                    {Array.isArray(stockResults) && stockResults.length > 15 && (
                                        <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-200">
                                            Showing top 15 results
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                    {stockLoading ? 'Searching...' : 'No stocks found'}
                                </div>
                            )}
                        </div>,
                        document.body
                    )}
                </div>
            ) : null}
        </div>
    )
}

// Memoized Checkbox Field Option Component to prevent unnecessary re-renders
const CheckboxFieldOption = React.memo(({ optionValue, optionLabel, isChecked, onToggle }) => {
    return (
        <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
                type="checkbox"
                checked={isChecked}
                onChange={onToggle}
                className="h-4 w-4 text-blue-600 rounded"
            />
            <span className="text-gray-700">{optionLabel}</span>
        </label>
    )
}, (prevProps, nextProps) => {
    // Only re-render if checked state or callbacks change
    return (
        prevProps.optionValue === nextProps.optionValue &&
        prevProps.optionLabel === nextProps.optionLabel &&
        prevProps.isChecked === nextProps.isChecked &&
        prevProps.onToggle === nextProps.onToggle
    )
})

CheckboxFieldOption.displayName = 'CheckboxFieldOption'

// Step 3: Condition Builder
const AlertConditionBuilder = ({ type, criteria, onChange }) => {
    // Unified state for all alert categories using new API
    const [categoryConfig, setCategoryConfig] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Transform new API format to old format
    const transformApiResponse = (apiConfig) => {
        if (!apiConfig) return null

        const transformed = { ...apiConfig }
        const inputDetails = apiConfig.input_details || {}

        // Field name mappings
        const fieldNameMappings = {
            'watchlist_changes': 'changes',
            'include_weekly_rebalancing': 'notifyOnRebalance',
            'running_time': 'running_time_section',
            'interval_during_market_hour': 'interval'  // Map to interval for backward compatibility
        }

        // Convert a single field from new format to old format
        const convertField = (newField) => {
            if (!newField) return null

            const oldField = {
                name: newField.name || newField.id,
                type: newField.type === 'multiselect' ? 'multi-select' :
                    newField.type === 'select' ? 'select' :
                        newField.type === 'checkbox' ? (newField.dataType === 'array' ? 'multi-select' : 'checkbox') :
                            newField.type === 'radio' ? 'radio' :
                                newField.type === 'textarea' ? 'textarea' :
                                    newField.type === 'text' ? 'text' :
                                        newField.type === 'number' ? 'number' :
                                            newField.type === 'composite' ? 'composite' : newField.type,
                input_type: newField.type === 'multiselect' ? 'tag-style-select' :
                    newField.type === 'checkbox' && newField.dataType === 'array' ? 'checkbox' :
                        newField.type === 'checkbox' ? 'checkbox' :
                            newField.type === 'radio' ? 'radio' :
                                newField.type === 'select' ? 'select' :
                                    newField.type === 'textarea' ? 'textarea' :
                                        newField.type === 'text' ? 'text' :
                                            newField.type === 'number' ? 'number' : newField.type,
                label: newField.label || newField.name,
                required: newField.required || false,
                default: newField.defaultValue !== undefined ? newField.defaultValue : (newField.default || ''),
                options: newField.options || [],
                choices: newField.options || [],
                description: newField.placeholder || newField.description || '',
                placeholder: newField.placeholder || '',
                rows: newField.rows || 3,
                min: newField.validation?.min,
                max: newField.validation?.max,
                step: newField.validation?.step,
                format: newField.format,
                subFields: newField.subFields,
                visibility: newField.visibility, // Preserve visibility for conditional rendering
                nested_fields: newField.visibility ? {} : undefined
            }

            return oldField
        }

        // For quantitative: transform each alert type's fields array
        if (apiConfig.category === 'quantitative') {
            const transformedInputDetails = {}
            Object.keys(inputDetails).forEach(alertTypeName => {
                const alertTypeData = inputDetails[alertTypeName]
                if (alertTypeData && alertTypeData.fields && Array.isArray(alertTypeData.fields)) {
                    // Convert fields array to object with field names as keys
                    const fieldsObj = {}
                    alertTypeData.fields.forEach(field => {
                        const fieldName = field.name || field.id
                        fieldsObj[fieldName] = convertField(field)
                    })
                    transformedInputDetails[alertTypeName] = fieldsObj
                } else {
                    // Fallback: use as-is if already in old format
                    transformedInputDetails[alertTypeName] = alertTypeData
                }
            })
            transformed.input_details = transformedInputDetails
            // Handle sub_category as array of objects {label, value} or array of strings
            const subCategoryArray = apiConfig.sub_category || []
            transformed.sub_categories = subCategoryArray.map(item =>
                typeof item === 'object' && item.value ? item.value : item
            )
            // Store original sub_category array for label access
            transformed.sub_category_objects = subCategoryArray
            // Store full sub_category for label access
            transformed.sub_category_objects = subCategoryArray
        } else {
            // For other alert types: transform fields array to object
            if (inputDetails.fields && Array.isArray(inputDetails.fields)) {
                const fieldsObj = {}
                inputDetails.fields.forEach(field => {
                    const fieldName = field.name || field.id
                    const mappedName = fieldNameMappings[fieldName] || fieldName
                    fieldsObj[mappedName] = convertField(field)
                })
                transformed.input_details = fieldsObj
            } else {
                // Fallback: use as-is if already in old format
                transformed.input_details = inputDetails
            }
        }

        return transformed
    }

    // Quantitative-specific states
    const [selectedAlertType, setSelectedAlertType] = useState(criteria.alertType || '')
    const [valueInput, setValueInput] = useState(() => {
        if (criteria.value !== undefined && criteria.value !== null) {
            return String(criteria.value)
        }
        return ''
    })
    // Local state for string inputs (parameter1, parameter2) to prevent focus loss
    const [stringInputs, setStringInputs] = useState(() => {
        const inputs = {}
        if (criteria.parameter1 !== undefined && criteria.parameter1 !== null) {
            inputs.parameter1 = String(criteria.parameter1)
        }
        if (criteria.parameter2 !== undefined && criteria.parameter2 !== null) {
            inputs.parameter2 = String(criteria.parameter2)
        }
        return inputs
    })
    const isUserInputRef = useRef(false)
    const isStringInputRef = useRef({ parameter1: false, parameter2: false })

    // Map alert type to API category parameter
    const getCategoryParam = (alertType) => {
        const mapping = {
            'quantitative': 'quantitative',
            'event': 'event',
            'content': 'content',
            'composition': 'screener',
            'technical_analysis': 'technical_analysis'
        }
        return mapping[alertType] || alertType
    }

    // Fetch alert category configuration from new API
    useEffect(() => {
        if (!type) return

        const categoryParam = getCategoryParam(type)
        const loadCategoryConfig = async () => {
            setLoading(true)
            setError(null)
            try {
                const data = await fetchAlertCategory(categoryParam)
                // Transform new API format to old format
                const transformedData = transformApiResponse(data)
                setCategoryConfig(transformedData)

                // For quantitative: Initialize default alert type if not already selected
                if (type === 'quantitative' && transformedData.sub_categories && transformedData.sub_categories.length > 0) {
                    if (!criteria.alertType) {
                        const firstType = transformedData.sub_categories[0]
                        setSelectedAlertType(firstType)
                        // Get default config from input_details
                        const alertTypeConfig = transformedData.input_details[firstType]
                        if (alertTypeConfig) {
                            onChange({
                                alertType: firstType,
                                operator: alertTypeConfig.operator?.default || '',
                                value: alertTypeConfig.value?.default !== undefined ? alertTypeConfig.value.default : '',
                                parameter1: alertTypeConfig.parameter1?.default || '',
                                parameter2: alertTypeConfig.parameter2?.default !== undefined ? alertTypeConfig.parameter2.default : null
                            })
                        }
                    } else if (criteria.alertType) {
                        setSelectedAlertType(criteria.alertType)
                    }
                }

                // For technical_analysis: Initialize default values if not already set
                if (type === 'technical_analysis' && transformedData.input_details) {
                    const inputDetails = transformedData.input_details
                    const runningTimeField = inputDetails.running_time_section || inputDetails.running_time
                    const needsInitialization = !criteria.running_time_section && !criteria.running_time

                    if (needsInitialization && runningTimeField?.default) {
                        const initialCriteria = { ...criteria }
                        // Set running_time_section with default value
                        initialCriteria.running_time_section = runningTimeField.default
                        // If default is 'during_market_hour', also set interval if it has a default
                        if (runningTimeField.default === 'during_market_hour') {
                            const intervalField = inputDetails.interval_during_market_hour || inputDetails.interval
                            if (intervalField?.default) {
                                initialCriteria.interval = intervalField.default
                                initialCriteria.interval_during_market_hour = intervalField.default
                            }
                        }
                        onChange(initialCriteria)
                    }
                }
            } catch (err) {
                console.error(`Failed to fetch alert category ${categoryParam}:`, err)
                setError(`Failed to load ${type} alert configuration`)
                setCategoryConfig(null)
            } finally {
                setLoading(false)
            }
        }
        loadCategoryConfig()
    }, [type, criteria.alertType, onChange])

    // Sync selectedAlertType with condition when condition changes externally
    useEffect(() => {
        if (type === 'quantitative' && criteria.alertType && criteria.alertType !== selectedAlertType) {
            setSelectedAlertType(criteria.alertType)
        }
    }, [criteria.alertType, type, selectedAlertType])

    // Sync valueInput for quantitative alerts
    useEffect(() => {
        if (type !== 'quantitative') return

        if (isUserInputRef.current) {
            isUserInputRef.current = false
            return
        }

        if (criteria.value !== undefined && criteria.value !== null) {
            setValueInput(String(criteria.value))
        } else if (categoryConfig?.input_details && selectedAlertType) {
            const alertTypeConfig = categoryConfig.input_details[selectedAlertType]
            const defaultValue = alertTypeConfig?.value?.default
            setValueInput(defaultValue !== undefined && defaultValue !== null ? String(defaultValue) : '')
        }
    }, [criteria.value, selectedAlertType, type, categoryConfig])

    // Sync stringInputs for quantitative alerts
    useEffect(() => {
        if (type !== 'quantitative') return

        // Sync parameter1
        if (!isStringInputRef.current.parameter1) {
            if (criteria.parameter1 !== undefined && criteria.parameter1 !== null) {
                setStringInputs(prev => ({ ...prev, parameter1: String(criteria.parameter1) }))
            } else if (categoryConfig?.input_details && selectedAlertType) {
                const alertTypeConfig = categoryConfig.input_details[selectedAlertType]
                const defaultValue = alertTypeConfig?.parameter1?.default
                if (defaultValue !== undefined && defaultValue !== null) {
                    setStringInputs(prev => ({ ...prev, parameter1: String(defaultValue) }))
                }
            }
        } else {
            isStringInputRef.current.parameter1 = false
        }

        // Sync parameter2
        if (!isStringInputRef.current.parameter2) {
            if (criteria.parameter2 !== undefined && criteria.parameter2 !== null) {
                setStringInputs(prev => ({ ...prev, parameter2: String(criteria.parameter2) }))
            } else if (categoryConfig?.input_details && selectedAlertType) {
                const alertTypeConfig = categoryConfig.input_details[selectedAlertType]
                const defaultValue = alertTypeConfig?.parameter2?.default
                if (defaultValue !== undefined && defaultValue !== null) {
                    setStringInputs(prev => ({ ...prev, parameter2: String(defaultValue) }))
                }
            }
        } else {
            isStringInputRef.current.parameter2 = false
        }
    }, [criteria.parameter1, criteria.parameter2, selectedAlertType, type, categoryConfig])

    // Sync textarea inputs when criteria values change externally (not from user typing)
    useEffect(() => {
        // Sync all textarea fields that are in the current criteria
        Object.keys(criteria).forEach(key => {
            const textareaKey = `${key}_${type || 'default'}`
            if (isTextareaInputRef.current[textareaKey] !== true) {
                const newValue = criteria[key] !== undefined && criteria[key] !== null ? String(criteria[key]) : ''
                if (textareaInputs[textareaKey] !== newValue) {
                    setTextareaInputs(prev => ({ ...prev, [textareaKey]: newValue }))
                }
            } else {
                isTextareaInputRef.current[textareaKey] = false
            }
        })
    }, [criteria, type])

    // Sync content alert URL input when currentSource.url changes externally (not from user typing)
    useEffect(() => {
        if (type === 'content') {
            const sources = criteria.sources || []
            const currentSource = sources[0] || { url: '', source_type: 'news' }
            if (!isContentUrlInputRef.current) {
                const newUrlValue = currentSource.url !== undefined && currentSource.url !== null ? String(currentSource.url) : ''
                setContentUrlInput(newUrlValue)
            } else {
                isContentUrlInputRef.current = false
            }
        }
    }, [criteria, type])

    // Helper to toggle array items
    const toggleItem = (currentList, item) => {
        const list = currentList || []
        if (list.includes(item)) {
            return list.filter(i => i !== item)
        }
        return [...list, item]
    }

    // Local state for textarea inputs to prevent focus loss
    const [textareaInputs, setTextareaInputs] = useState({})
    const isTextareaInputRef = useRef({})
    // Local state for content alert URL field to prevent focus loss
    const [contentUrlInput, setContentUrlInput] = useState('')
    const isContentUrlInputRef = useRef(false)

    // Dynamic field renderer - renders any field type from API response
    const renderField = (fieldName, fieldConfig, fieldValue, onFieldChange, parentValue = null) => {
        if (!fieldConfig) return null

        const {
            name,
            type: fieldType,
            input_type,
            label,
            required,
            default: defaultValue,
            options = [],
            validation = {},
            placeholder,
            rows,
            description
        } = fieldConfig

        const currentValue = fieldValue !== undefined && fieldValue !== null ? fieldValue : (defaultValue !== undefined ? defaultValue : '')

        // Handle multi-select (checkbox style)
        if (fieldType === 'multi-select' && (input_type === 'checkbox' || !input_type)) {
            const selectedValues = Array.isArray(currentValue) ? currentValue : []
            // Support both 'options' and 'choices' for compatibility
            const fieldOptions = options.length > 0 ? options : (fieldConfig.choices || [])

            if (fieldOptions.length === 0) return null

            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label || fieldName} {required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {fieldOptions.map(option => {
                            // Handle both object format {value, label} and string format
                            const optionValue = typeof option === 'object' ? option.value : option
                            const optionLabel = typeof option === 'object' ? option.label : option
                            const isChecked = selectedValues.includes(optionValue)

                            return (
                                <CheckboxFieldOption
                                    key={optionValue}
                                    optionValue={optionValue}
                                    optionLabel={optionLabel}
                                    isChecked={isChecked}
                                    onToggle={() => onFieldChange(toggleItem(selectedValues, optionValue))}
                                />
                            )
                        })}
                    </div>
                    {description && (
                        <p className="text-xs text-gray-500 mt-1">{description}</p>
                    )}
                </div>
            )
        }

        // Handle tag-style multi-select
        if (fieldType === 'multi-select' && input_type === 'tag-style-select') {
            const selectedValues = Array.isArray(currentValue) ? currentValue : []
            const availableOptions = options.filter(opt => !selectedValues.includes(opt.value))

            const handleSelect = (value) => {
                if (value && !selectedValues.includes(value)) {
                    onFieldChange([...selectedValues, value])
                }
            }

            const handleRemove = (valueToRemove) => {
                onFieldChange(selectedValues.filter(v => v !== valueToRemove))
            }

            const getLabel = (value) => {
                const option = options.find(opt => opt.value === value)
                return option ? option.label : value
            }

            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    {selectedValues.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                            {selectedValues.map(value => (
                                <span
                                    key={value}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                >
                                    {getLabel(value)}
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(value)}
                                        className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                        aria-label={`Remove ${getLabel(value)}`}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    {availableOptions.length > 0 ? (
                        <select
                            value=""
                            onChange={(e) => {
                                if (e.target.value) {
                                    handleSelect(e.target.value)
                                    e.target.value = ''
                                }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select {label.toLowerCase()} to add...</option>
                            {availableOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    ) : selectedValues.length > 0 ? (
                        <p className="text-sm text-gray-500 mt-2">All options have been selected</p>
                    ) : null}
                </div>
            )
        }

        // Handle select dropdown
        if (fieldType === 'select' && input_type === 'select') {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                        value={currentValue}
                        onChange={(e) => {
                            const newValue = e.target.value
                            onFieldChange(newValue)
                            // Handle conditional clearing (e.g., interval when market_end is selected)
                            if (parentValue === 'market_end' && fieldName === 'interval') {
                                onFieldChange(null)
                            }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Select {label.toLowerCase()}...</option>
                        {options.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {description && (
                        <p className="text-xs text-gray-500 mt-1">{description}</p>
                    )}
                </div>
            )
        }

        // Handle radio buttons
        if (fieldType === 'radio' && input_type === 'radio') {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="space-y-2">
                        {options.map(option => (
                            <label key={option.value} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="radio"
                                    name={name || fieldName}
                                    value={option.value}
                                    checked={currentValue === option.value}
                                    onChange={(e) => {
                                        const newValue = e.target.value
                                        onFieldChange(newValue)
                                    }}
                                    className="h-4 w-4 text-blue-600"
                                />
                                <span className="text-gray-700">{option.label}</span>
                            </label>
                        ))}
                    </div>
                    {description && (
                        <p className="text-xs text-gray-500 mt-1">{description}</p>
                    )}
                </div>
            )
        }

        // Handle textarea - with smooth typing to prevent focus loss
        if (fieldType === 'textarea' && input_type === 'textarea') {
            // Use local state for smooth typing
            const textareaKey = `${fieldName}_${type || 'default'}`
            const currentTextareaValue = textareaInputs[textareaKey] !== undefined
                ? textareaInputs[textareaKey]
                : (currentValue !== undefined && currentValue !== null ? String(currentValue) : '')

            const handleTextareaChange = (e) => {
                const inputValue = e.target.value
                isTextareaInputRef.current[textareaKey] = true // Mark as user input
                // Only update local state while typing - don't call onFieldChange to prevent re-renders
                setTextareaInputs(prev => ({ ...prev, [textareaKey]: inputValue }))
            }

            const handleTextareaBlur = (e) => {
                const inputValue = e.target.value
                isTextareaInputRef.current[textareaKey] = false // Reset flag
                // Update parent state on blur
                onFieldChange(inputValue)
            }

            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label} {!required && <span className="text-gray-500 text-xs">(Optional)</span>}
                    </label>
                    <textarea
                        value={currentTextareaValue}
                        onChange={handleTextareaChange}
                        onBlur={handleTextareaBlur}
                        placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
                        rows={rows || 4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            )
        }

        // Handle text input
        if (fieldType === 'text' && input_type === 'text') {
            return (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label} {required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => onFieldChange(e.target.value)}
                        placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>
            )
        }

        // Handle checkbox
        if (fieldType === 'checkbox' && (input_type === 'checkbox' || !input_type)) {
            return (
                <div className="mt-4">
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={currentValue || false}
                            onChange={(e) => onFieldChange(e.target.checked)}
                            className="rounded text-blue-600"
                        />
                        <span className="text-sm text-gray-700">{label || fieldName}</span>
                    </label>
                    {description && (
                        <p className="text-xs text-gray-500 mt-1">{description}</p>
                    )}
                </div>
            )
        }

        return null
    }

    // Render nested fields (e.g., url field inside source_type option)
    const renderNestedFields = (nestedFields, parentValue, parentFieldName) => {
        if (!nestedFields || Object.keys(nestedFields).length === 0) return null

        return Object.entries(nestedFields).map(([nestedFieldName, nestedFieldConfig]) => {
            // Check if nested field should be visible based on parent value
            if (parentFieldName === 'source_type' && parentValue === 'news' && nestedFieldName === 'url') {
                return null // URL field is hidden for 'news' source type
            }

            const nestedValue = criteria[nestedFieldName] !== undefined
                ? criteria[nestedFieldName]
                : (criteria.sources?.[0]?.[nestedFieldName] !== undefined
                    ? criteria.sources[0][nestedFieldName]
                    : undefined)

            const handleNestedChange = (value) => {
                if (parentFieldName === 'source_type') {
                    // Handle content source nested fields
                    const sources = criteria.sources || []
                    const currentSource = sources[0] || { url: '', source_type: 'news' }
                    const updatedSource = { ...currentSource, [nestedFieldName]: value }
                    onChange({ ...criteria, sources: [updatedSource] })
                } else {
                    // Handle other nested fields (e.g., interval)
                    onChange({ ...criteria, [nestedFieldName]: value })
                }
            }

            return (
                <div key={nestedFieldName}>
                    {renderField(nestedFieldName, nestedFieldConfig, nestedValue, handleNestedChange, parentValue)}
                </div>
            )
        })
    }

    if (type === 'quantitative') {
        if (loading) {
            return (
                <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading alert types...</p>
                </div>
            )
        }

        if (error || !categoryConfig) {
            return (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-600">{error || 'Failed to load alert configuration'}</p>
                </div>
            )
        }

        const inputDetails = categoryConfig.input_details || {}
        // Handle sub_category as array of objects {label, value} or array of strings
        const subCategoryArray = categoryConfig.sub_category || categoryConfig.sub_category_objects || []
        const alertTypes = subCategoryArray.map(item =>
            typeof item === 'object' && item.value ? item.value : item
        )
        // Create label map for display
        const alertTypeLabels = {}
        subCategoryArray.forEach(item => {
            if (typeof item === 'object' && item.value) {
                alertTypeLabels[item.value] = item.label
            } else {
                alertTypeLabels[item] = item
            }
        })

        // Operator labels mapping for display
        const operatorLabels = {
            '>': 'Greater Than (>)',
            '>=': 'Greater Than or Equal (>=)',
            '<': 'Less Than (<)',
            '<=': 'Less Than or Equal (<=)',
            '=': 'Equal (=)',
            'crossover': 'Crossover',
            'crossover_above': 'Crossover Above',
            'crossover_below': 'Crossover Below',
            'touch_upper': 'Touch Upper Band',
            'touch_lower': 'Touch Lower Band',
            'breakout_above': 'Breakout Above',
            'breakout_below': 'Breakout Below',
            'squeeze': 'Squeeze',
            'divergence_bullish': 'Bullish Divergence',
            'divergence_bearish': 'Bearish Divergence'
        }

        const handleAlertTypeChange = (alertTypeName) => {
            setSelectedAlertType(alertTypeName)
            const alertTypeConfig = inputDetails[alertTypeName]
            if (alertTypeConfig) {
                const defaultValue = alertTypeConfig.value?.default
                setValueInput(defaultValue !== undefined && defaultValue !== null ? String(defaultValue) : '')
                onChange({
                    ...criteria,
                    alertType: alertTypeName,
                    operator: alertTypeConfig.operator?.default || '',
                    value: defaultValue !== undefined ? defaultValue : '',
                    parameter1: alertTypeConfig.parameter1?.default || '',
                    parameter2: alertTypeConfig.parameter2?.default !== undefined ? alertTypeConfig.parameter2.default : null
                })
            }
        }

        const currentAlertTypeConfig = selectedAlertType ? inputDetails[selectedAlertType] : null

        // Render quantitative field (value, operator, parameter1, parameter2)
        const renderQuantitativeField = (fieldName, fieldConfig) => {
            if (!fieldConfig) return null

            // Special handling for value field (smooth typing)
            if (fieldName === 'value') {
                if (fieldConfig.required === false && !fieldConfig.default) return null

                const handleChange = (e) => {
                    const inputValue = e.target.value
                    isUserInputRef.current = true
                    setValueInput(inputValue)
                }

                const handleBlur = (e) => {
                    const inputValue = e.target.value.trim()
                    isUserInputRef.current = false

                    if (inputValue === '') {
                        const defaultValue = fieldConfig.default !== undefined ? fieldConfig.default : ''
                        setValueInput(defaultValue !== '' ? String(defaultValue) : '')
                        onChange({ ...criteria, value: defaultValue })
                        return
                    }

                    const numValue = parseFloat(inputValue)
                    if (!isNaN(numValue)) {
                        let finalValue = numValue
                        if (fieldConfig.min !== undefined && finalValue < fieldConfig.min) {
                            finalValue = fieldConfig.min
                        }
                        if (fieldConfig.max !== undefined && finalValue > fieldConfig.max) {
                            finalValue = fieldConfig.max
                        }
                        setValueInput(String(finalValue))
                        onChange({ ...criteria, value: finalValue })
                    } else {
                        const defaultValue = fieldConfig.default !== undefined ? fieldConfig.default : ''
                        setValueInput(defaultValue !== '' ? String(defaultValue) : '')
                        onChange({ ...criteria, value: defaultValue })
                    }
                }

                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Value {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <input
                            type="number"
                            step={fieldConfig.step !== undefined ? fieldConfig.step : (fieldConfig.type === 'decimal' ? '0.1' : '1')}
                            min={fieldConfig.min}
                            max={fieldConfig.max}
                            value={valueInput}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={fieldConfig.description || 'e.g. 150'}
                        />
                        {fieldConfig.description && (
                            <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                        )}
                    </div>
                )
            }

            // Special handling for operator field (get choices from fieldConfig)
            if (fieldName === 'operator') {
                // Support both old format (choices array) and new format (options array with objects)
                const fieldOptions = fieldConfig.options || fieldConfig.choices || []
                if (fieldOptions.length === 0) return null

                // Convert to unified format: array of {value, label} objects
                const operatorOptions = fieldOptions.map(opt => {
                    if (typeof opt === 'object' && opt.value) {
                        // New format: {value, label}
                        return {
                            value: opt.value,
                            label: operatorLabels[opt.value] || opt.label || opt.value
                        }
                    } else {
                        // Old format: string value
                        return {
                            value: opt,
                            label: operatorLabels[opt] || opt
                        }
                    }
                })

                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Operator {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                            value={criteria.operator || fieldConfig.default || ''}
                            onChange={(e) => onChange({ ...criteria, operator: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {operatorOptions.map((op) => (
                                <option key={op.value} value={op.value}>
                                    {op.label}
                                </option>
                            ))}
                        </select>
                        {fieldConfig.description && (
                            <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                        )}
                    </div>
                )
            }

            // Special handling for parameter2 with format "period,std_dev"
            if (fieldName === 'parameter2' && fieldConfig.format === 'period,std_dev') {
                const currentValue = criteria.parameter2 || fieldConfig.default || '20,2'
                const [period, stdDev] = currentValue.split(',')

                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {fieldConfig.description?.split(' ')[0] || 'Parameter 2'} {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Period</label>
                                <input
                                    type="number"
                                    value={period || ''}
                                    onChange={(e) => {
                                        const newValue = `${e.target.value || ''},${stdDev || '2'}`
                                        onChange({ ...criteria, parameter2: newValue })
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="20"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Std Dev</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={stdDev || ''}
                                    onChange={(e) => {
                                        const newValue = `${period || '20'},${e.target.value || ''}`
                                        onChange({ ...criteria, parameter2: newValue })
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="2"
                                />
                            </div>
                        </div>
                        {fieldConfig.description && (
                            <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                        )}
                    </div>
                )
            }

            // Handle choice/select type (for parameter1 and parameter2)
            if ((fieldConfig.type === 'choice' || fieldConfig.type === 'select') && (fieldConfig.choices || fieldConfig.options)) {
                const fieldOptions = fieldConfig.options || fieldConfig.choices || []
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {fieldConfig.label || fieldConfig.description?.split(' ')[0] || fieldName} {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                            value={criteria[fieldName] || fieldConfig.default || ''}
                            onChange={(e) => onChange({ ...criteria, [fieldName]: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {fieldOptions.map((choice) => {
                                // Support both object format {value, label} and string format
                                const optionValue = typeof choice === 'object' ? choice.value : choice
                                const optionLabel = typeof choice === 'object' ? choice.label : choice
                                return (
                                    <option key={optionValue} value={optionValue}>{optionLabel}</option>
                                )
                            })}
                        </select>
                        {fieldConfig.description && (
                            <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                        )}
                    </div>
                )
            }

            // Handle options (for parameter1 and parameter2)
            if (fieldConfig.options) {
                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {fieldConfig.description?.split(' ')[0] || fieldName} {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                            value={criteria[fieldName] || fieldConfig.default || ''}
                            onChange={(e) => onChange({ ...criteria, [fieldName]: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {fieldConfig.options.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        {fieldConfig.description && (
                            <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                        )}
                    </div>
                )
            }

            // Handle string type (text input) - with smooth typing to prevent focus loss
            if (fieldConfig.type === 'string') {
                if (fieldConfig.required === false && !fieldConfig.default) return null

                const currentStringValue = stringInputs[fieldName] !== undefined
                    ? stringInputs[fieldName]
                    : (criteria[fieldName] !== undefined && criteria[fieldName] !== null
                        ? String(criteria[fieldName])
                        : (fieldConfig.default !== undefined && fieldConfig.default !== null
                            ? String(fieldConfig.default)
                            : ''))

                const handleStringChange = (e) => {
                    const inputValue = e.target.value
                    isStringInputRef.current[fieldName] = true // Mark as user input
                    // Only update local state while typing - don't call onChange to prevent re-renders
                    setStringInputs(prev => ({ ...prev, [fieldName]: inputValue }))
                }

                const handleStringBlur = (e) => {
                    const inputValue = e.target.value.trim()
                    isStringInputRef.current[fieldName] = false // Reset flag

                    if (inputValue === '') {
                        const defaultValue = fieldConfig.default !== undefined && fieldConfig.default !== null
                            ? String(fieldConfig.default)
                            : ''
                        setStringInputs(prev => ({ ...prev, [fieldName]: defaultValue }))
                        onChange({ ...criteria, [fieldName]: defaultValue || null })
                        return
                    }

                    // Update parent state on blur
                    onChange({ ...criteria, [fieldName]: inputValue })
                }

                return (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {fieldConfig.description?.split(' ')[0] || fieldName} {fieldConfig.required && <span className="text-red-500">*</span>}
                        </label>
                        <input
                            type="text"
                            value={currentStringValue}
                            onChange={handleStringChange}
                            onBlur={handleStringBlur}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={fieldConfig.description || `Enter ${fieldName}`}
                        />
                    </div>
                )
            }

            return null
        }

        return (
            <div className="space-y-6">
                {alertTypes.length > 0 && (
                    <>
                        {/* Alert Type Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Alert Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={selectedAlertType}
                                onChange={(e) => handleAlertTypeChange(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select Alert Type...</option>
                                {alertTypes.map((alertTypeName) => (
                                    <option key={alertTypeName} value={alertTypeName}>
                                        {alertTypeLabels[alertTypeName] || alertTypeName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedAlertType && currentAlertTypeConfig && (
                            <div className="space-y-4">
                                {renderQuantitativeField('operator', currentAlertTypeConfig.operator)}
                                {renderQuantitativeField('parameter1', currentAlertTypeConfig.parameter1)}
                                {/* Hide parameter2 for gap_trading and price_deviation */}
                                {selectedAlertType !== 'gap_trading' && selectedAlertType !== 'price_deviation' && renderQuantitativeField('parameter2', currentAlertTypeConfig.parameter2)}
                                {renderQuantitativeField('value', currentAlertTypeConfig.value)}
                            </div>
                        )}
                    </>
                )}
            </div>
        )
    }

    if (type === 'event') {
        if (loading) {
            return (
                <div className="text-center py-4 text-gray-500">Loading event types...</div>
            )
        }

        if (error || !categoryConfig) {
            return (
                <div className="text-center py-4 text-red-500 text-sm">{error || 'Failed to load event configuration'}</div>
            )
        }

        const inputDetails = categoryConfig.input_details || {}
        const eventsField = inputDetails.events

        if (!eventsField) {
            return (
                <div className="text-center py-4 text-gray-500 text-sm">No event types available</div>
            )
        }

        return (
            <div className="space-y-4">
                {renderField('events', eventsField, criteria.events, (value) => onChange({ ...criteria, events: value }))}
            </div>
        )
    }

    if (type === 'content') {
        if (loading) {
            return (
                <div className="text-center py-4 text-gray-500">Loading content configuration...</div>
            )
        }

        if (error || !categoryConfig) {
            return (
                <div className="text-center py-4 text-red-500 text-sm">{error || 'Failed to load content configuration'}</div>
            )
        }

        const inputDetails = categoryConfig.input_details || {}
        const sources = criteria.sources || []
        const currentSource = sources[0] || { url: '', source_type: 'news' }

        const updateSource = (updates) => {
            const updatedSource = { ...currentSource, ...updates }
            // For 'news', ensure URL is empty string
            if (updates.source_type === 'news') {
                updatedSource.url = ''
            }
            // Always store as array with single element
            onChange({ ...criteria, sources: [updatedSource] })
        }

        const sourceTypeField = inputDetails.source_type
        const urlField = inputDetails.url
        const basePromptField = inputDetails.base_prompt
        const mentionSymbolsOnlyField = inputDetails.mention_symbols_only

        // Check visibility condition for URL field: show when source_type is in ["youtube", "blog", "social", "other"]
        const currentSourceType = currentSource.source_type || sourceTypeField?.default || 'news'
        const urlFieldVisibility = urlField?.visibility?.dependsOn?.[0]
        const showUrlField = urlField && (
            !urlFieldVisibility ||
            (urlFieldVisibility.fieldId === 'source_type' &&
                urlFieldVisibility.condition === 'in' &&
                urlFieldVisibility.values?.includes(currentSourceType))
        )

        return (
            <div className="space-y-6">
                {/* Content Source */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">Content Source</h4>
                    <div className="space-y-3">
                        {sourceTypeField && (
                            <div>
                                <label className="block text-xs font-medium text-blue-800 mb-1">{sourceTypeField.label}</label>
                                <select
                                    value={currentSource.source_type || sourceTypeField.default || 'news'}
                                    onChange={(e) => {
                                        updateSource({ source_type: e.target.value })
                                    }}
                                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                                >
                                    {sourceTypeField.options?.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Render URL field based on visibility condition */}
                        {showUrlField && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {urlField.label} {urlField.required && <span className="text-red-500">*</span>}
                                </label>
                                <input
                                    type="text"
                                    value={contentUrlInput}
                                    onChange={(e) => {
                                        const inputValue = e.target.value
                                        isContentUrlInputRef.current = true // Mark as user input
                                        // Only update local state while typing - don't call updateSource to prevent re-renders
                                        setContentUrlInput(inputValue)
                                    }}
                                    onBlur={(e) => {
                                        const inputValue = e.target.value
                                        isContentUrlInputRef.current = false // Reset flag
                                        // Update parent state on blur
                                        updateSource({ url: inputValue })
                                    }}
                                    placeholder={urlField.placeholder || 'https://...'}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Analysis Configuration */}
                {basePromptField && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <h4 className="text-sm font-medium text-green-900 mb-3">Analysis Configuration</h4>
                        <div className="space-y-3">
                            {renderField('base_prompt', basePromptField, criteria.base_prompt, (value) => onChange({ ...criteria, base_prompt: value }))}
                        </div>
                    </div>
                )}

                {/* Mention Symbols Only */}
                {mentionSymbolsOnlyField && (
                    <div>
                        {renderField('mention_symbols_only', mentionSymbolsOnlyField, criteria.mention_symbols_only !== undefined ? criteria.mention_symbols_only : (mentionSymbolsOnlyField.default !== undefined ? mentionSymbolsOnlyField.default : true), (value) => onChange({ ...criteria, mention_symbols_only: value }))}
                    </div>
                )}
            </div>
        )
    }

    if (type === 'composition') {
        if (loading) {
            return (
                <div className="text-center py-4 text-gray-500">Loading composition configuration...</div>
            )
        }

        if (error || !categoryConfig) {
            return (
                <div className="text-center py-4 text-red-500 text-sm">{error || 'Failed to load composition configuration'}</div>
            )
        }

        // Support both formats: fields in input_details OR fields at top level
        const inputDetails = categoryConfig.input_details || {}
        let changesField = inputDetails.changes || categoryConfig.changes
        let notifyOnRebalanceField = inputDetails.notifyOnRebalance || categoryConfig.notifyOnRebalance

        // Normalize field structure for top-level fields (add missing properties)
        if (changesField && !changesField.name) {
            changesField = {
                name: 'changes',
                label: 'Alert on Watchlist Changes',
                input_type: 'checkbox',
                ...changesField,
                // Convert 'choices' to 'options' if present
                options: changesField.choices || changesField.options || []
            }
        }

        if (notifyOnRebalanceField && !notifyOnRebalanceField.name) {
            notifyOnRebalanceField = {
                name: 'notifyOnRebalance',
                label: 'Include weekly rebalancing updates',
                input_type: 'checkbox',
                ...notifyOnRebalanceField
            }
        }

        return (
            <div className="space-y-4">
                {changesField && renderField('changes', changesField, criteria.changes, (value) => onChange({ ...criteria, changes: value }))}
                {notifyOnRebalanceField && renderField('notifyOnRebalance', notifyOnRebalanceField, criteria.notifyOnRebalance, (value) => onChange({ ...criteria, notifyOnRebalance: value }))}
            </div>
        )
    }

    if (type === 'technical_analysis') {
        if (loading) {
            return (
                <div className="text-center py-4 text-gray-500">Loading technical analysis configuration...</div>
            )
        }

        if (error || !categoryConfig) {
            return (
                <div className="text-center py-4 text-red-500 text-sm">{error || 'Failed to load technical analysis configuration'}</div>
            )
        }

        const inputDetails = categoryConfig.input_details || {}
        const patternsField = inputDetails.patterns
        const chartTimeHorizonField = inputDetails.chart_time_horizon
        // Handle both running_time (new) and running_time_section (old)
        const runningTimeSectionField = inputDetails.running_time_section || inputDetails.running_time
        const intervalField = inputDetails.interval_during_market_hour || inputDetails.interval
        const userPromptField = inputDetails.user_prompt

        // Check both field names for interval visibility, including default value from field config
        const runningTimeValue = criteria.running_time_section || criteria.running_time || runningTimeSectionField?.default
        const showIntervalField = runningTimeValue === 'during_market_hour'

        return (
            <div className="space-y-6">
                {patternsField && renderField('patterns', patternsField, criteria.patterns, (value) => onChange({ ...criteria, patterns: value }))}
                {chartTimeHorizonField && renderField('chart_time_horizon', chartTimeHorizonField, criteria.chart_time_horizon, (value) => onChange({ ...criteria, chart_time_horizon: value }))}
                {runningTimeSectionField && (
                    <div>
                        {renderField('running_time_section', runningTimeSectionField, runningTimeValue, (value) => {
                            const newCriteria = { ...criteria, running_time_section: value }
                            // Clear interval if Market End is selected
                            if (value === 'market_end') {
                                newCriteria.interval = null
                                newCriteria.interval_during_market_hour = null
                            }
                            onChange(newCriteria)
                        })}
                        {/* Render interval field when during_market_hour is selected */}
                        {showIntervalField && intervalField && (
                            <div className="mt-4">
                                {renderField('interval', intervalField, criteria.interval || criteria.interval_during_market_hour, (value) => {
                                    onChange({ ...criteria, interval: value, interval_during_market_hour: value })
                                })}
                            </div>
                        )}
                    </div>
                )}
                {userPromptField && renderField('user_prompt', userPromptField, criteria.user_prompt, (value) => onChange({ ...criteria, user_prompt: value }))}
            </div>
        )
    }

    if (type === 'analyst_agent') {
        return (
            <AnalystAgentConditionBuilder
                criteria={criteria}
                onChange={onChange}
            />
        )
    }

    return <div className="text-gray-500 italic">Configuration for {type} alerts coming soon...</div>
}

const AnalystAgentConditionBuilder = ({ criteria, onChange }) => {
    const capabilities = [
        { id: 'deep-researcher', label: 'Market & Fundamental Research', description: 'Web, Social Media & News research + Earnings' },
        { id: 'technical-analyst', label: 'Technical Analysis', description: 'Chart patterns & technical indicators' },
        { id: 'screener-agent', label: 'Stock Screening', description: 'Screen stocks based on criteria' },
        { id: 'watchlist-agent', label: 'Watchlist Management', description: 'Create and update watchlists based on research' },
        { id: 'pdf-agent', label: 'PDF & Report Analysis', description: 'Financial statements & technical PDFs' },
    ]

    const promptTemplates = [
        { label: 'Market Sentiment', text: 'Analyze the current market sentiment for the selected target. Look at recent news and social media trends.' },
        { label: 'Technical Breakdown', text: 'Perform a detailed technical analysis for the selected target. Identify key support/resistance and chart patterns.' },
        { label: 'Earnings Analysis', text: 'Analyze the latest earnings report and performance for the selected target. Compare it with analyst expectations.' },
    ]

    const selectedCapabilities = criteria.capabilities || []

    const toggleCapability = (id) => {
        const newCapabilities = selectedCapabilities.includes(id)
            ? selectedCapabilities.filter(c => c !== id)
            : [...selectedCapabilities, id]
        onChange({ ...criteria, capabilities: newCapabilities })
    }

    return (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Agent Capabilities</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {capabilities.map(cap => (
                        <label key={cap.id} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedCapabilities.includes(cap.id)}
                                onChange={() => toggleCapability(cap.id)}
                                className="mt-1 h-4 w-4 text-blue-600 rounded"
                            />
                            <div>
                                <div className="text-sm font-medium text-gray-900">{cap.label}</div>
                                <div className="text-xs text-gray-500">{cap.description}</div>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Master Prompt</label>
                    <div className="flex gap-2">
                        {promptTemplates.map(template => (
                            <button
                                key={template.label}
                                onClick={() => onChange({ ...criteria, master_prompt: template.text })}
                                className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded"
                            >
                                {template.label}
                            </button>
                        ))}
                    </div>
                </div>
                <textarea
                    value={criteria.master_prompt || ''}
                    onChange={(e) => onChange({ ...criteria, master_prompt: e.target.value })}
                    placeholder="Describe what you want the agent to do..."
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
                    <select
                        value={criteria.schedule || 'daily'}
                        onChange={(e) => onChange({ ...criteria, schedule: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notification Condition</label>
                    <select
                        value={criteria.notification_condition || 'always'}
                        onChange={(e) => onChange({ ...criteria, notification_condition: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                        <option value="always">Always notify</option>
                        <option value="on_action">Notify on action taken</option>
                    </select>
                </div>
            </div>
        </div>
    )
}

// Memoize AlertConditionBuilder to prevent unnecessary re-renders
const MemoizedAlertConditionBuilder = React.memo(AlertConditionBuilder, (prevProps, nextProps) => {
    // Only re-render if type or criteria actually changed
    return (
        prevProps.type === nextProps.type &&
        JSON.stringify(prevProps.criteria) === JSON.stringify(nextProps.criteria) &&
        prevProps.onChange === nextProps.onChange
    )
})

MemoizedAlertConditionBuilder.displayName = 'MemoizedAlertConditionBuilder'

// Step 4: Notification Config
const AlertNotificationConfig = ({ config, onChange, formData }) => {
    // Generate default name based on alert type and configuration
    const generateDefaultName = () => {
        const { type, target, criteria } = formData

        if (type === 'content') {
            const sources = criteria?.sources || []
            const source = sources[0] || {}
            const sourceType = source.source_type || 'news'
            const sourceTypeLabel = sourceType === 'news' ? 'News' :
                sourceType === 'youtube' ? 'YouTube' :
                    sourceType.charAt(0).toUpperCase() + sourceType.slice(1)

            if (target.type === 'watchlist' && target.name) {
                return `${sourceTypeLabel} Monitoring - ${target.name}`
            } else if (target.type === 'stock' && target.stocks && target.stocks.length > 0) {
                const stockNames = target.stocks.map(s => s.symbol || s).slice(0, 3).join(', ')
                const more = target.stocks.length > 3 ? ` +${target.stocks.length - 3} more` : ''
                return `${sourceTypeLabel} Monitoring - ${stockNames}${more}`
            } else {
                return `${sourceTypeLabel} Monitoring Alert`
            }
        } else if (type === 'quantitative') {
            if (target.type === 'watchlist' && target.name) {
                return `${target.name} - Quantitative Alert`
            } else if (target.type === 'stock' && target.stocks && target.stocks.length > 0) {
                const stockNames = target.stocks.map(s => s.symbol || s).slice(0, 2).join(', ')
                return `${stockNames} - Quantitative Alert`
            } else {
                return 'Quantitative Alert'
            }
        } else if (type === 'event') {
            if (target.type === 'watchlist' && target.name) {
                return `${target.name} - Event Alert`
            } else if (target.type === 'stock' && target.stocks && target.stocks.length > 0) {
                const stockNames = target.stocks.map(s => s.symbol || s).slice(0, 2).join(', ')
                return `${stockNames} - Event Alert`
            } else {
                return 'Event Alert'
            }
        } else if (type === 'composition') {
            if (target.type === 'watchlist' && target.name) {
                return `${target.name} - Composition Alert`
            } else {
                return 'Watchlist Composition Alert'
            }
        } else if (type === 'analyst_agent') {
            if (target.type === 'broad') {
                return 'Broad Market Research Alert'
            } else if (target.type === 'watchlist' && target.name) {
                return `${target.name} - Analyst Agent Research`
            } else if (target.type === 'stock' && target.stocks && target.stocks.length > 0) {
                const stockNames = target.stocks.map(s => s.symbol || s).slice(0, 2).join(', ')
                return `${stockNames} - Analyst Agent Research`
            } else {
                return 'Analyst Agent Alert'
            }
        }

        return 'New Alert'
    }

    const defaultName = generateDefaultName()
    const displayName = config.name || defaultName

    // Set default name when component mounts or when relevant formData changes (only if name is empty)
    useEffect(() => {
        // Only set default name if name is empty or not set
        // This allows user to delete and customize the name
        if (!config.name || config.name.trim() === '') {
            const defaultName = generateDefaultName()
            if (defaultName) {
                onChange({ ...config, name: defaultName })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formData.type,
        formData.target?.type,
        formData.target?.name,
        formData.target?.id,
        formData.target?.stocks?.length,
        formData.criteria?.sources?.length
    ])

    return (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Notification Channels</label>
                <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center space-x-3">
                            <Mail className="w-5 h-5 text-blue-600" />
                            <div>
                                <div className="font-medium text-gray-900">Email</div>
                                <div className="text-xs text-gray-500">Send to registered email</div>
                            </div>
                        </div>
                        <input
                            type="checkbox"
                            checked={config.email}
                            onChange={(e) => onChange({ ...config, email: e.target.checked })}
                            className="h-5 w-5 text-blue-600 rounded"
                        />
                    </label>

                    <label className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center space-x-3">
                            <MessageCircle className="w-5 h-5 text-blue-600" />
                            <div>
                                <div className="font-medium text-gray-900">WhatsApp</div>
                                <div className="text-xs text-gray-500">Send to connected WhatsApp number</div>
                            </div>
                        </div>
                        <input
                            type="checkbox"
                            checked={config.whatsapp}
                            onChange={(e) => onChange({ ...config, whatsapp: e.target.checked })}
                            className="h-5 w-5 text-blue-600 rounded"
                        />
                    </label>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Name</label>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => {
                        const newName = e.target.value
                        // If user clears the input, don't set it to empty, keep the default
                        onChange({ ...config, name: newName === defaultName ? '' : newName })
                    }}
                    placeholder={defaultName}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default name is auto-generated based on your configuration. You can edit it.</p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cooldown Period</label>
                <select
                    value={config.cooldown || '1h'}
                    onChange={(e) => onChange({ ...config, cooldown: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                    <option value="none">No cooldown (notify every time)</option>
                    <option value="1h">1 hour</option>
                    <option value="24h">24 hours</option>
                    <option value="7d">7 days</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Prevent spam by limiting how often this alert can trigger.</p>
            </div>
        </div>
    )
}

const AlertWizard = ({ isOpen, onClose, onSave }) => {
    const [step, setStep] = useState(1)
    const [formData, setFormData] = useState({
        type: null,
        target: { type: 'watchlist', id: null, name: '', stocks: [] },
        criteria: {},
        notifications: { email: true, whatsapp: false, name: '', cooldown: '24h' }
    })
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)

    // Memoize the criteria onChange handler to prevent unnecessary re-renders
    const handleCriteriaChange = useCallback((criteria) => {
        setFormData(prevFormData => ({ ...prevFormData, criteria }))
    }, [])

    if (!isOpen) return null

    // Validate step 2: At least one symbol or symbol group must be selected
    const isStep2Valid = () => {
        if (step !== 2) return true // Only validate step 2

        const { type, id, stocks } = formData.target

        if (type === 'broad') {
            // Broad market search is always valid if type is analyst_agent
            return formData.type === 'analyst_agent'
        }

        if (type === 'watchlist') {
            // Must have a watchlist selected
            return id !== null && id !== undefined
        } else if (type === 'stock') {
            // Must have at least one stock selected
            return stocks && stocks.length > 0
        }

        return false
    }

    const handleNext = () => {
        // Validate step 2 before proceeding
        if (step === 2 && !isStep2Valid()) {
            setToast({
                message: 'Please select at least one watchlist or add at least one symbol',
                type: 'error'
            })
            return
        }
        setStep(step + 1)
    }
    const handleBack = () => setStep(step - 1)

    const handleSave = async () => {
        // Handle content alert type
        if (formData.type === 'content') {
            // Get sources array from criteria, use empty array if not set
            const sources = formData.criteria?.sources || []

            // Validate at least one source is configured
            // If sources is empty, use default source
            const source = sources.length > 0 ? sources[0] : { url: '', source_type: 'news' }

            // Validate source_type exists
            if (!source || !source.source_type) {
                setToast({ message: 'Please configure the content source', type: 'error' })
                return
            }

            // For non-news sources, URL is required
            if (source.source_type !== 'news' && !source.url) {
                setToast({ message: 'Please provide a URL for the content source', type: 'error' })
                return
            }

            // Get symbols and symbolgroup from target
            const symbols = formData.target.type === 'stock'
                ? formData.target.stocks?.map(s => s.id) || []
                : []
            const symbolgroup = formData.target.type === 'watchlist'
                ? formData.target.id || null
                : null

            // Create payload for single API call to /alerts/
            // Backend will create ContentSource, AnalysisProcess, and UserAlert in one request
            const alertPayload = {
                type: formData.type, // 'content'
                target: {
                    type: formData.target.type, // 'watchlist' or 'stock'
                    stocks: symbols, // Array of symbol IDs (if type is 'stock')
                    id: symbolgroup // SymbolGroup ID (if type is 'watchlist')
                },
                criteria: {
                    sources: sources.map(source => ({
                        url: source.url || '',
                        source_type: source.source_type || 'news'
                    })),
                    base_prompt: formData.criteria?.base_prompt || '',
                    mention_symbols_only: formData.criteria?.mention_symbols_only !== undefined ? formData.criteria.mention_symbols_only : true
                },
                notifications: {
                    name: formData.notifications?.name || '',
                    email: formData.notifications?.email || false,
                    telegram: formData.notifications?.telegram || false,
                    cooldown: formData.notifications?.cooldown || '24h'
                }
            }

            // ==========================================
            // CONSOLE LOG: Show payload being sent
            // ==========================================
            console.log('==========================================')
            console.log('📋 CONTENT ALERT PAYLOAD:')
            console.log(JSON.stringify(alertPayload, null, 2))
            console.log('==========================================')

            // Call API to create content alert (single request creates ContentSource, AnalysisProcess, and UserAlert)
            setSaving(true)
            setToast(null)

            try {
                const response = await createContentAlert(alertPayload)

                // ==========================================
                // CONSOLE LOG: Show response received
                // ==========================================
                console.log('==========================================')
                console.log('✅ RESPONSE RECEIVED FROM API:', JSON.stringify(response, null, 2))
                console.log('Response Status:', response.status)
                console.log('Response Data:', response.data)
                console.log('==========================================')

                // Success - status 201
                if (response.status === 201) {
                    setToast({
                        message: response.message || 'Content alert created successfully',
                        type: 'success'
                    })

                    // Call onSave with the created alert data
                    onSave(response.data)

                    // Close modal after a short delay to show success message
                    setTimeout(() => {
                        onClose()
                        // Reset form when closing
                        setStep(1)
                        setFormData({
                            type: null,
                            target: { type: 'watchlist', id: null, name: '', stocks: [] },
                            criteria: {
                                source: null,
                                base_prompt: ''
                            },
                            notifications: { email: true, telegram: false, name: '', cooldown: '24h' }
                        })
                    }, 1500)
                }
            } catch (error) {
                // ==========================================
                // CONSOLE LOG: Show error details
                // ==========================================
                console.log('==========================================')
                console.log('❌ ERROR OCCURRED:', error)
                console.log('Error Message:', error.message)
                console.log('Error Response:', error.response)
                console.log('Error Data:', error.response?.data)
                console.log('Error Status:', error.response?.status)
                console.log('==========================================')

                // Error handling
                const errorMessage = error.message || 'Failed to create content alert'
                setToast({
                    message: errorMessage,
                    type: 'error'
                })
            } finally {
                setSaving(false)
            }
            return
        }

        // ==========================================
        // CONSOLE LOG: Show all form data (for non-content types)
        // ==========================================
        console.log('==========================================')
        console.log('📋 COMPLETE FORMDATA:', JSON.stringify(formData, null, 2))
        console.log('==========================================')

        // Handle analyst agent alert type
        if (formData.type === 'analyst_agent') {
            // Validate required fields
            if (!formData.criteria.capabilities || formData.criteria.capabilities.length === 0) {
                setToast({ message: 'Please select at least one agent capability', type: 'error' })
                return
            }
            if (!formData.criteria.master_prompt) {
                setToast({ message: 'Please provide a master prompt for the agent', type: 'error' })
                return
            }

            // Construct payload
            const alertPayload = {
                type: 'analyst_agent',
                target: {
                    type: formData.target.type, // 'watchlist', 'stock', or 'broad'
                    stocks: formData.target.type === 'stock' ? formData.target.stocks.map(s => s.id) : [],
                    id: formData.target.type === 'watchlist' ? formData.target.id : null
                },
                criteria: {
                    capabilities: formData.criteria.capabilities,
                    master_prompt: formData.criteria.master_prompt,
                    schedule: formData.criteria.schedule || 'daily',
                    notification_condition: formData.criteria.notification_condition || 'always'
                },
                notifications: {
                    name: formData.notifications?.name || 'Analyst Agent Alert',
                    email: formData.notifications?.email || true,
                    whatsapp: formData.notifications?.whatsapp || false,
                    cooldown: formData.notifications?.cooldown || '24h'
                }
            }

            console.log('📋 ANALYST AGENT ALERT PAYLOAD:', JSON.stringify(alertPayload, null, 2))

            setSaving(true)
            setToast(null)

            try {
                const response = await createAlert(alertPayload)
                console.log('✅ RESPONSE RECEIVED:', JSON.stringify(response, null, 2))

                if (response.status === 201) {
                    setToast({ message: 'Analyst Agent alert created successfully', type: 'success' })
                    onSave(response.data)
                    setTimeout(() => {
                        onClose()
                        setStep(1)
                        setFormData({
                            type: null,
                            target: { type: 'watchlist', id: null, name: '', stocks: [] },
                            criteria: {},
                            notifications: { email: true, whatsapp: false, name: '', cooldown: '24h' }
                        })
                    }, 1500)
                }
            } catch (error) {
                console.error('❌ ERROR:', error)
                setToast({ message: error.response?.data?.error || 'Failed to create analyst agent alert', type: 'error' })
            } finally {
                setSaving(false)
            }
            return
        }

        // Only proceed if alert type is 'event', 'quantitative' or 'composition'
        if (formData.type !== 'event' && formData.type !== 'quantitative' && formData.type !== 'composition' && formData.type !== 'technical_analysis') {
            onSave(formData)
            onClose()
            return
        }

        // Validate required fields - Target
        if (formData.target.type === 'watchlist' && !formData.target.id) {
            setToast({ message: 'Please select a watchlist', type: 'error' })
            return
        }

        if (formData.target.type === 'stock' && (!formData.target.stocks || formData.target.stocks.length === 0)) {
            setToast({ message: 'Please select at least one stock', type: 'error' })
            return
        }

        // Validate required fields - Criteria (Event)
        if (formData.type === 'event') {
            if (!formData.criteria.events || formData.criteria.events.length === 0) {
                setToast({ message: 'Please select at least one event type', type: 'error' })
                return
            }
        }

        // Validate required fields - Criteria (Composition)
        if (formData.type === 'composition') {
            if (!formData.criteria.changes || formData.criteria.changes.length === 0) {
                setToast({ message: 'Please select at least one change type', type: 'error' })
                return
            }
        }

        // Validate required fields - Criteria (Quantitative)
        if (formData.type === 'quantitative') {
            if (!formData.criteria.alertType) {
                setToast({ message: 'Please select an alert type', type: 'error' })
                return
            }
            if (!formData.criteria.operator) {
                setToast({ message: 'Please select an operator', type: 'error' })
                return
            }
            // Check if value is required for this specific alert type
            // For crossover operators, value is typically not required
            const crossoverOperators = ['crossover', 'crossover_above', 'crossover_below']
            const isCrossoverOperator = crossoverOperators.includes(formData.criteria.operator)

            // If not a crossover operator, check if value is provided
            // For crossover operators, value is optional (can be null/empty)
            if (!isCrossoverOperator && (formData.criteria.value === undefined || formData.criteria.value === null || formData.criteria.value === '')) {
                setToast({ message: 'Please enter a value', type: 'error' })
                return
            }
            if (!formData.criteria.parameter1) {
                setToast({ message: 'Please select parameter 1', type: 'error' })
                return
            }
        }

        // Validate required fields - Criteria (Technical Analysis)
        if (formData.type === 'technical_analysis') {
            if (!formData.criteria.patterns || formData.criteria.patterns.length === 0) {
                setToast({ message: 'Please select at least one pattern', type: 'error' })
                return
            }
            if (!formData.criteria.chart_time_horizon) {
                setToast({ message: 'Please select a chart time horizon', type: 'error' })
                return
            }
            if (!formData.criteria.running_time_section) {
                setToast({ message: 'Please select a running time section', type: 'error' })
                return
            }
            // Interval is required only when running_time_section is 'during_market_hour'
            if (formData.criteria.running_time_section === 'during_market_hour') {
                if (!formData.criteria.interval) {
                    setToast({ message: 'Please select an interval during market hour', type: 'error' })
                    return
                }
            }
        }

        setSaving(true)
        setToast(null)

        try {
            // Generate default alert name if not provided
            const getDefaultAlertName = () => {
                const typeLabels = {
                    'quantitative': 'Quantitative Alert',
                    'event': 'Event Alert',
                    'technical_analysis': 'Technical Analysis Alert',
                    'content': 'Content Alert',
                    'composition': 'Composition Alert'
                }
                const typeLabel = typeLabels[formData.type] || 'Alert'
                const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19)
                return `${typeLabel} - ${timestamp}`
            }

            // Transform formData to API payload format
            let payload = {
                type: formData.type,
                target: formData.target.type === 'watchlist'
                    ? { id: formData.target.id }
                    : { stocks: formData.target.stocks.map(s => s.id) },
                notifications: {
                    email: formData.notifications.email,
                    whatsapp: formData.notifications.whatsapp,
                    name: formData.notifications.name || getDefaultAlertName(),
                    cooldown: formData.notifications.cooldown
                }
            }

            // Add criteria-specific fields (all step 3 data in criteria object for consistency)
            if (formData.type === 'event') {
                payload.criteria = {
                    events: formData.criteria.events
                }
            } else if (formData.type === 'composition') {
                // Composition alerts: events and include_weekly_rebalancing go inside criteria object
                payload.criteria = {
                    events: formData.criteria.changes || [],
                    include_weekly_rebalancing: formData.criteria.notifyOnRebalance || false
                }
            } else if (formData.type === 'quantitative') {
                payload.criteria = {
                    alertType: formData.criteria.alertType,
                    operator: formData.criteria.operator,
                    value: formData.criteria.value,
                    parameter1: formData.criteria.parameter1,
                    parameter2: formData.criteria.parameter2
                }
            } else if (formData.type === 'technical_analysis') {
                payload.criteria = {
                    patterns: formData.criteria.patterns,
                    chart_time_horizon: formData.criteria.chart_time_horizon,
                    running_time_section: formData.criteria.running_time_section,
                    interval_during_market_hour: formData.criteria.interval || null,
                    user_prompt: formData.criteria.user_prompt || null
                }
            }

            const response = await createAlert(payload)

            // ==========================================
            // CONSOLE LOG: Show response received
            // ==========================================
            console.log('==========================================')
            console.log('✅ RESPONSE RECEIVED FROM API:', JSON.stringify(response, null, 2))
            console.log('Response Status:', response.status)
            console.log('Response Data:', response.data)
            console.log('==========================================')

            // Success - status 201
            if (response.status === 201) {
                setToast({
                    message: response.message || 'Alert created successfully',
                    type: 'success'
                })

                // Call onSave with the created alert data
                onSave(response.data)

                // Close modal after a short delay to show success message
                setTimeout(() => {
                    onClose()
                    // Reset form when closing
                    setStep(1)
                    setFormData({
                        type: null,
                        target: { type: 'watchlist', id: null, name: '', stocks: [] },
                        criteria: {
                            source: null,
                            base_prompt: ''
                        },
                        notifications: { email: true, telegram: false, name: '', cooldown: '24h' }
                    })
                }, 1500)
            }
        } catch (error) {
            // ==========================================
            // CONSOLE LOG: Show error details
            // ==========================================
            console.log('==========================================')
            console.log('❌ ERROR OCCURRED:', error)
            console.log('Error Message:', error.message)
            console.log('Error Response:', error.response)
            console.log('Error Data:', error.response?.data)
            console.log('Error Status:', error.response?.status)
            console.log('==========================================')

            // Error handling
            const errorMessage = error.message || 'Failed to create alert'
            setToast({
                message: errorMessage,
                type: 'error'
            })
        } finally {
            setSaving(false)
        }
    }

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return <AlertTypeSelector
                    selectedType={formData.type}
                    onSelect={(type) => {
                        let newTarget = formData.target
                        if (type === 'composition' && formData.target.type !== 'watchlist') {
                            newTarget = { type: 'watchlist', id: null, name: '', stocks: [] }
                        }
                        setFormData({ ...formData, type, target: newTarget })
                        // Initialize criteria.sources with default source for content type
                        const newFormData = { ...formData, type }
                        if (type === 'content' && (!newFormData.criteria?.sources || newFormData.criteria.sources.length === 0)) {
                            newFormData.criteria = {
                                ...newFormData.criteria,
                                sources: [{ url: '', source_type: 'news' }],
                                base_prompt: newFormData.criteria?.base_prompt || '',
                                mention_symbols_only: newFormData.criteria?.mention_symbols_only !== undefined ? newFormData.criteria.mention_symbols_only : true
                            }
                        }
                        setFormData(newFormData)
                        handleNext()
                    }}
                />
            case 2:
                return <AlertTargetSelector
                    target={formData.target}
                    onChange={(target) => setFormData({ ...formData, target })}
                    alertType={formData.type}
                />
            case 3:
                return <MemoizedAlertConditionBuilder
                    type={formData.type}
                    criteria={formData.criteria}
                    onChange={handleCriteriaChange}
                />
            case 4:
                return <AlertNotificationConfig
                    config={formData.notifications}
                    formData={formData}
                    onChange={(notifications) => setFormData({ ...formData, notifications })}
                    alertType={formData.type}
                />
            default:
                return null
        }
    }

    const getStepTitle = () => {
        switch (step) {
            case 1: return 'Choose Alert Type'
            case 2: return 'Select Target'
            case 3: return 'Define Conditions'
            case 4: return 'Configure Notifications'
            default: return ''
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 p-6 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{getStepTitle()}</h2>
                        <p className="text-sm text-gray-500">Step {step} of 4</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-100 h-1">
                    <div
                        className="bg-blue-600 h-1 transition-all duration-300"
                        style={{ width: `${(step / 4) * 100}%` }}
                    />
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {renderStepContent()}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 flex justify-between bg-gray-50 rounded-b-lg">
                    {step > 1 ? (
                        <NeutralButton
                            label="← Back"
                            onClick={handleBack}
                        />
                    ) : (
                        <div />
                    )}

                    {step < 4 ? (
                        <NeutralButton
                            label="Next →"
                            onClick={handleNext}
                            disabled={(step === 1 && !formData.type) || (step === 2 && !isStep2Valid())}
                        />
                    ) : (
                        <Button
                            label={saving ? 'Creating...' : 'Create Alert'}
                            onClick={handleSave}
                            disabled={saving}
                        />
                    )}
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    )
}

// Memoize AlertWizard to prevent unnecessary re-renders from parent
const MemoizedAlertWizard = React.memo(AlertWizard, (prevProps, nextProps) => {
    // Only re-render if props actually changed
    return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.onClose === nextProps.onClose &&
        prevProps.onSave === nextProps.onSave
    )
})

MemoizedAlertWizard.displayName = 'MemoizedAlertWizard'

export default MemoizedAlertWizard
